import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import SEOHead, { websiteSchema, organizationSchema, localBusinessSchema } from "./components/SEOHead";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollProgress from "./components/ScrollProgress";
import HomePage from "./pages/HomePage";
import "@/App.css";

const ServicePage = lazy(() => import("./pages/ServicePage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const LocalPage = lazy(() => import("./pages/LocalPage"));
const EntreprisesPage = lazy(() => import("./pages/EntreprisesPage"));
const VehiclesPage = lazy(() => import("./pages/VehiclesPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const ArticleEditor = lazy(() => import("./pages/admin/ArticleEditor"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppLayout({ children, showFooter = true }) {
  return (
    <>
      <Header />
      <main className="min-h-screen pt-20">{children}</main>
      {showFooter && <Footer />}
    </>
  );
}

function LazyFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#070707]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin" />
        <span className="text-zinc-500 text-sm">Chargement...</span>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route path="/" element={<AppLayout><HomePage /></AppLayout>} />
        <Route path="/vendre-voiture-montelimar" element={<AppLayout><ServicePage slug="vendre-voiture" /></AppLayout>} />
        <Route path="/estimer-voiture" element={<AppLayout><ServicePage slug="estimer-vehicule" /></AppLayout>} />
        <Route path="/acheter-voiture-montelimar" element={<VehiclesPage />} />
        <Route path="/vehicule/:slug" element={<VehiclesPage />} />
        <Route path="/depot-vente-montelimar" element={<AppLayout><ServicePage slug="depot-vente" /></AppLayout>} />
        <Route path="/blog" element={<AppLayout><BlogPage /></AppLayout>} />
        <Route path="/blog/:slug" element={<AppLayout><ArticlePage /></AppLayout>} />
        <Route path="/blog/categorie/:slug" element={<AppLayout><CategoryPage /></AppLayout>} />
        <Route path="/contact" element={<AppLayout><ContactPage /></AppLayout>} />
        <Route path="/a-propos" element={<AppLayout><AboutPage /></AppLayout>} />
        <Route path="/faq" element={<AppLayout><FAQPage /></AppLayout>} />
        <Route path="/mentions-legales" element={<AppLayout><LegalPage type="mentions" /></AppLayout>} />
        <Route path="/politique-confidentialite" element={<AppLayout><LegalPage type="privacy" /></AppLayout>} />
        <Route path="/avertissement-editorial" element={<AppLayout><LegalPage type="disclaimer" /></AppLayout>} />
        <Route path="/zone/:slug" element={<AppLayout><LocalPage /></AppLayout>} />
        <Route path="/entreprises" element={<AppLayout><EntreprisesPage /></AppLayout>} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/articles/new" element={<ArticleEditor />} />
        <Route path="/admin/articles/:id/edit" element={<ArticleEditor />} />
        <Route path="*" element={<AppLayout><NotFoundPage /></AppLayout>} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <ScrollProgress />
        <Toaster position="top-right" theme="dark" />
        <SEOHead schemas={[websiteSchema, organizationSchema, localBusinessSchema]} />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
